import Error404Page from 'app/pages/misc/Error404Page';
import RequireAuth from 'app/shared-components/auth/RequireAuth';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import LoginSSO from './pages/auth/LoginSSO';
import Logout from './pages/auth/Logout';
import MobileLanding from './pages/auth/MobileLanding';
import PasskeyPrompt from './pages/auth/PasskeyPrompt';
import SessionExpiredPage from 'app/pages/auth/SessionExpiredPage';
import MobileReviewPage from './pages/mobile/pages/mobile-review/MobileReviewPage';
import MobileLoadingOverlay from './pages/mobile/components/MobileLoadingOverlay';
import MobilePageLayout from './pages/mobile/mobile-layout/MobilePageLayout';
import MobileHomePage from './pages/mobile/pages/mobile-home/MobileHomePage';
import MobileForgotPassword from './pages/mobile/pages/mobile-login/MobileForgotPassword';
import MobileSignIn from './pages/mobile/pages/mobile-login/MobileSignIn';
import MobilePage from './pages/mobile/pages/mobile-page/MobilePage';
import MobileSettings from './pages/mobile/pages/mobile-settings/MobileSettings';
import MobileTransactions from './pages/mobile/pages/mobile-transactions/MobileTransactions';
import MobileReimbursements from './pages/mobile/pages/mobile-reimbursements/MobileReimbursements';
import PurchaseCardsWrapper from './pages/PurchaseCardsWrapper';
import FeatureFlagProvider, { useIsEnabled } from './shared-components/feature-flags/FeatureFlagProvider';
import { DEFAULT_LANDING_PAGE } from './store/authSlice';
import { useAppSelector } from './store';
import { selectUserCompanies } from './store/userCompaniesSlice';

const MobileCardRouter = lazy(() => import('./pages/mobile/pages/mobile-card/MobileCardRouter'));

const Register = lazy(() => import('app/pages/auth/register/Register'));
const VerifyUser = lazy(() => import('app/pages/auth/VerifyUser'));
const OnBoard = lazy(() => import('./pages/onboard/OnBoard'));

// Reimbursements require both the feature flag AND the paidol having them
// enabled in App Settings (reimbursementSettings.isEnabled).
function ConditionalReimbursementsRoute(): JSX.Element {
  const { 'reimbursementsFlags.showReimbursements': showReimbursements } = useIsEnabled([
    { featureName: 'reimbursementsFlags', flagName: 'showReimbursements' },
  ]);
  const { selectedPaidolId, selectedPaidol } = useAppSelector(selectUserCompanies);

  // Wait for the flag to resolve and for the paidol to hydrate before deciding —
  // otherwise we'd bounce the user to /landing during the initial paidol fetch.
  const isPaidolLoading = !!selectedPaidolId && !selectedPaidol;
  if (showReimbursements === undefined || isPaidolLoading) {
    return <MobileLoadingOverlay isLoading={true} />;
  }

  const isReimbursementsEnabled = !!showReimbursements && !!selectedPaidol?.reimbursementSettings?.isEnabled;
  return isReimbursementsEnabled ? <MobileReimbursements /> : <Navigate to="/landing" replace />;
}

function MobileRoutingTable(): JSX.Element {
  return (
    <FeatureFlagProvider>
      <Routes>
        <Route path="/" element={<Navigate to={DEFAULT_LANDING_PAGE} />} />
        <Route
          path="/"
          element={
            <RequireAuth requireUser={false} requireOnboarded={false} isMobile={true}>
              <Suspense fallback={<MobileLoadingOverlay isLoading={true} />}>
                <Outlet />
              </Suspense>
            </RequireAuth>
          }
        >
          {/* Mobile Auth Routes */}
          <Route path="/login" element={<MobileSignIn />} />
          <Route path="/forgot-password" element={<MobileForgotPassword />} />
          <Route path="/forgot-password/:username" element={<MobileForgotPassword />} />
          <Route path="/mobile-page" element={<MobilePage />} />

          {/* Legacy SSO and Registration */}
          <Route path="/login/sso" element={<LoginSSO />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/:email" element={<Register />} />
          <Route path="/verify-user" element={<VerifyUser />} />
          <Route path="/verify-user/:username" element={<VerifyUser />} />
          <Route path="/onboard/*" element={<OnBoard />} />
        </Route>

        {/* SEE-214: post-login passkey enrollment prompt on iOS. MobileSignIn
            redirects eligible users here right after sign-in; MobileLanding
            covers reloads/deep-links. Rendered OUTSIDE MobilePageLayout — a
            full-screen auth-style page, not a dashboard view — and with
            requireOnboarded/requireFeatureFlags off so it shows on auth alone
            and never waits behind the companies + feature-flag spinner. */}
        <Route
          path="/setup-passkey"
          element={
            <RequireAuth requireOnboarded={false} requireFeatureFlags={false} isMobile={true}>
              <PasskeyPrompt />
            </RequireAuth>
          }
        />

        {/* SEE-219: post-inactivity-logout message screen. Same placement
            rationale as the desktop RoutingTable: deliberately outside every
            RequireAuth group — the guard navigates here while the sign-out is
            still settling. Missing from this router, the mobile shell rendered
            the 404 catch-all instead of the logged-out message. */}
        <Route path="/logged-out" element={<SessionExpiredPage />} />

        <Route
          path="/"
          element={
            <RequireAuth isMobile={true}>
              <PurchaseCardsWrapper isMobileApp={true}>
                <Suspense fallback={<MobileLoadingOverlay isLoading={true} />}>
                  <MobilePageLayout>
                    <Outlet />
                  </MobilePageLayout>
                </Suspense>
              </PurchaseCardsWrapper>
            </RequireAuth>
          }
        >
          <Route path="/landing" element={<MobileLanding />} />
          <Route path="/mobile-demo" element={<MobileHomePage />} />
          <Route path="/transactions" element={<MobileTransactions />} />
          <Route path="/transactions/:id" element={<MobileTransactions />} />
          <Route path="/reimbursements" element={<ConditionalReimbursementsRoute />} />
          <Route path="/settings/*" element={<MobileSettings />} />
          <Route path="/card/*" element={<MobileCardRouter />} />
          <Route path="/logout" element={<Logout />} />
        </Route>
        <Route
          path="/review/*"
          element={
            <RequireAuth requireOnboarded={false} isMobile={true}>
              <MobileReviewPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Error404Page />} />
      </Routes>
    </FeatureFlagProvider>
  );
}

export default MobileRoutingTable;
